import { Venue } from '@/components/home/types'
import { isBefore, parseISO, isWithinInterval, subWeeks, addWeeks, format } from 'date-fns'

type VenueHoursStatus = {
  isOpen: boolean
  opensAt: string | null
}

type BasicScheduleEntry = {
  day_of_week: number
}

type DetailedScheduleEntry = {
  day_of_week: number
  open_datetime_utc: string
  close_datetime_utc: string
  open_time_utc: string
  close_time_utc: string
}

type LocalDateTimeScheduleEntry = {
  open_datetime: Date
  close_datetime: Date
}

type ScheduleEntry = BasicScheduleEntry | DetailedScheduleEntry

const padScheduleList = (thisWeekScheduleList: LocalDateTimeScheduleEntry[]): LocalDateTimeScheduleEntry[] => {
  const previousWeekScheduleList = thisWeekScheduleList.map((entry: LocalDateTimeScheduleEntry) => {
    const lastWeekOpenDateTime = subWeeks(entry.open_datetime, 1)
    const lastWeekCloseDateTime = subWeeks(entry.close_datetime, 1)
    return { open_datetime: lastWeekOpenDateTime, close_datetime: lastWeekCloseDateTime }
  })

  const nextWeekScheduleList = thisWeekScheduleList.map((entry: LocalDateTimeScheduleEntry) => {
    const nextWeekOpenDateTime = addWeeks(entry.open_datetime, 1)
    const nextWeekCloseDateTime = addWeeks(entry.close_datetime, 1)
    return { open_datetime: nextWeekOpenDateTime, close_datetime: nextWeekCloseDateTime }
  })

  return [...previousWeekScheduleList, ...thisWeekScheduleList, ...nextWeekScheduleList]
}

const sortAllScheduleEntries = (
  scheduleListOne: ScheduleEntry[],
  scheduleListTwo: ScheduleEntry[]
): DetailedScheduleEntry[] => {
  // Combine the two lists
  const combinedScheduleList = [...scheduleListOne, ...scheduleListTwo]

  // Filter out BasicScheduleEntries
  const filteredScheduleList = combinedScheduleList.filter(
    (entry): entry is DetailedScheduleEntry => 'open_datetime_utc' in entry
  )

  // Sort the filtered list
  filteredScheduleList.sort((a, b) => {
    const dateA = parseISO(a.open_datetime_utc)
    const dateB = parseISO(b.open_datetime_utc)

    if (isBefore(dateA, dateB)) {
      return -1
    } else if (isBefore(dateB, dateA)) {
      return 1
    }
    return 0
  })

  return filteredScheduleList
}

const makeScheduleListIntoLocalDateTimes = (scheduleList: DetailedScheduleEntry[]): LocalDateTimeScheduleEntry[] => {
  const localDateTimeSchedule = scheduleList.map((scheduleEntry) => {
    // DetailedScheduleEntry
    const openDateTimeUTC = parseISO(scheduleEntry.open_datetime_utc + 'Z').toISOString()
    const openDateTimeLocal = new Date(openDateTimeUTC)
    const closeDateTimeUTC = parseISO(scheduleEntry.close_datetime_utc + 'Z').toISOString()
    const closeDateTimeLocal = new Date(closeDateTimeUTC)
    return { open_datetime: openDateTimeLocal, close_datetime: closeDateTimeLocal }
  })

  return localDateTimeSchedule
}

const findCurrentAndNextOpenSchedule = (now: Date, scheduleList: LocalDateTimeScheduleEntry[]) => {
  let currentScheduleEntry: LocalDateTimeScheduleEntry | null = null
  let nextScheduleEntry: LocalDateTimeScheduleEntry | null = null

  for (let i = 0; i < scheduleList.length; i++) {
    const entry = scheduleList[i]
    if (isWithinInterval(now, { start: entry.open_datetime, end: entry.close_datetime })) {
      currentScheduleEntry = entry
      nextScheduleEntry = scheduleList[i + 1] || null // this shouldn't really happen because we padded the scheduleList
      break
    }

    // If there is no current schedule and 'now' is before the entry's open time,
    // it means this is the next upcoming schedule
    if (!currentScheduleEntry && isBefore(now, entry.open_datetime)) {
      nextScheduleEntry = entry
      break
    }
  }

  return { currentScheduleEntry, nextScheduleEntry }
}

const findVenueOpensAt = (now: Date, venue: Venue): VenueHoursStatus => {
  // venues have 2 schedules for split time. a venue can be open 9am -- 11am, then 2pm -- 8pm
  const scheduleListOne = venue.schedule1
  const scheduleListTwo = venue.schedule2

  // combine and sort schedule 1 and schedule 2
  const sortedScheduleEntries = sortAllScheduleEntries(scheduleListOne, scheduleListTwo)

  // convert scheule entries from UTC to System Local Time
  const localScheduleList = makeScheduleListIntoLocalDateTimes(sortedScheduleEntries)

  // pad with a week behind and a week ahead so we can always find nextScheduleEntry
  const localScheduleListPadded = padScheduleList(localScheduleList)

  // find the schedule entry that 'now' fits into and/or the next possible schedule entry
  const { currentScheduleEntry, nextScheduleEntry } = findCurrentAndNextOpenSchedule(now, localScheduleListPadded)

  // if we are inside a schedule Entry, we're open!
  if (currentScheduleEntry) {
    return { isOpen: true, opensAt: '' }
  }
  // if we are not, let's find the next closest opening time!
  else if (nextScheduleEntry) {
    return { isOpen: false, opensAt: `Opens ${format(nextScheduleEntry.open_datetime, "EEEE 'at' h:mm a")}` }
  }
  // Something has gone horribly wrong. fallback to open to be safe. Most users are only using this inside the venue :)
  else {
    return { isOpen: true, opensAt: '' }
  }
}

export const getVenueOpensStatus = (venue: Venue): VenueHoursStatus => {
  // if we don't have the venue schedule we're closed
  if (!venue?.schedule1) {
    return { isOpen: false, opensAt: null }
  }

  const now = new Date()
  const { isOpen, opensAt } = findVenueOpensAt(now, venue)

  return { isOpen, opensAt }
}
